import { FieldTimeOutlined, ToolOutlined } from '@ant-design/icons';
import { Button, Card, Col, InputNumber, Row, Select, Table, Tooltip } from 'antd';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { updatePmOfferWorkPlaceItem } from '../../../../../../contextRedux/PMOffer/PmOfferModel';
import { CustomTable } from '../../../../../../customComponents/table/CustomTable';
import { RootState } from '../../../../../../store';
import { Column } from '../../../../../../utils/column';
import './DevicesTable.css';

const { Option } = Select;

const DevicesTable: React.FC<any> = () => {
  const isMobile = useMediaQuery({ query: '(max-width: 480px)' });
  const dispatch = useDispatch();
  const addedDevices = useSelector((state: RootState) => state?.models?.pmOffer.pmOfferWorkPlaceItems);
  const pmOffer = useSelector((state: RootState) => state?.models?.pmOffer);

  const handleDeviceChange = (record: any, value: any, dataIndex: string) => {
    const updatedRecord = { ...record, [dataIndex]: value };

    dispatch(
      updatePmOfferWorkPlaceItem({
        index: addedDevices.findIndex(
          (item: any) => item.deviceTypeId === record.deviceTypeId && item.workPlaceId === record.workPlaceId
        ),
        updates: updatedRecord,
      })
    );
  };

  const handleUnitChange = (record: any, newUnit: string) => {
    // Find the index of the record in addedDevices
    const recordIndex = addedDevices.findIndex(
      (item: any) => item.deviceTypeId === record.deviceTypeId && item.workPlaceId === record.workPlaceId
    );

    // If the record is found, proceed with the update
    if (recordIndex !== -1) {
      const updatedRecord = { ...addedDevices[recordIndex] };

      if (newUnit === 'hour') {
        updatedRecord.laborTime = Number.parseFloat((60 * updatedRecord.piece)?.toFixed(2));
        updatedRecord.laborPrice = Number.parseFloat(
          (pmOffer.laborTab.laborPriceOnHour ?? 300 * updatedRecord.piece)?.toFixed(2)
        );
        updatedRecord.laborPriceOnHour = Number.parseFloat((pmOffer.laborTab.laborPriceOnHour ?? 300).toFixed(2));
      } else {
        const device = updatedRecord.device ? { ...updatedRecord.device } : {};

        updatedRecord.laborTime = Number.parseFloat(
          ((device.laborTimePM + (device.totalLaborsTimePM ?? 0)) * updatedRecord.piece)?.toFixed(2)
        );
        updatedRecord.laborPrice = Number.parseFloat(
          ((device.laborPricePM + (device.totalLaborsPricePM ?? 0)) * updatedRecord.piece)?.toFixed(2)
        );
      }

      // Update the unit measure
      updatedRecord.unitMeasure = newUnit;

      // Dispatch the update for the specific record in the Redux state
      dispatch(
        updatePmOfferWorkPlaceItem({
          index: addedDevices.findIndex(
            (item: any) => item.deviceTypeId === record.deviceTypeId && item.workPlaceId === record.workPlaceId
          ),
          updates: updatedRecord,
        })
      );
    }
  };

  const renderColumn = (column: any, _text: string, record: any) => {
    const value = Array.isArray(column.dataIndex)
      ? column.dataIndex.reduce((acc: any, key: any) => acc[key], record)
      : record[column.dataIndex];

    if (column.editable) {
      return (
        <div className='diableButton-input-container'>
          {column.dataIndex === 'laborPrice' && (
            <Button disabled className='disableButton'>
              <b>
                <ToolOutlined
                  style={{
                    marginRight: 5,
                  }}
                />
                {record.unitMeasure == 'buc'
                  ? Number.parseFloat((record.laborPrice / record.piece).toFixed(2)) + ' lei/buc'
                  : Number.parseFloat((record.laborPrice / record.piece).toFixed(2)) + ' lei/ora'}
              </b>
            </Button>
          )}
          {column.dataIndex === 'laborTime' && (
            <Button disabled className='disableButton'>
              <b>
                <FieldTimeOutlined
                  style={{
                    marginRight: 5,
                  }}
                />
                {record.unitMeasure == 'buc' ? record.device.laborTimePM + ' min/buc' : ''}
              </b>
            </Button>
          )}
          <InputNumber
            min={1}
            value={value}
            disabled={column.dataIndex === 'piece'}
            onChange={(value) => handleDeviceChange(record, value, column.dataIndex)}
            className='disableButton'
          />
          {column.dataIndex === 'piece' && (
            <Select
              value={record?.unitMeasure || 'buc'}
              onChange={(newUnit: string) => handleUnitChange(record, newUnit)}
              className='input-width'
            >
              <Option value='buc'>buc</Option>
              <Option value='hour'>hour</Option>
            </Select>
          )}
        </div>
      );
    }
    return value;
  };

  const calculateWeightedAverage = (unitMeasure: string) => {
    let sumWeightedCosts = 0;
    let laborTimePerPieces = 0;

    addedDevices
      .filter((ad) => ad.unitMeasure == unitMeasure)
      .forEach((item) => {
        const { laborTime, laborPrice, piece } = item;

        sumWeightedCosts += Number.parseFloat((piece * (((laborPrice / piece) * 60) / (laborTime / piece))).toFixed(2));
        laborTimePerPieces += piece;
      });
    return Number.parseFloat((sumWeightedCosts / laborTimePerPieces).toFixed(2));
  };

  const columnsFirstTable: Column[] = [
    {
      title: 'No.',
      key: 'index',
      dataIndex: 'index',
      render: (_text: any, _record: any, index: any) => <div>{index + 1 + '.'}</div>,
    },
    {
      title: 'Device',
      key: 'device',
      width: 200,
      render: (record) => (
        <div style={{ display: 'grid' }}>
          <Tooltip title={record.device?.name}>
            <div className='ellipsis-cell'>
              <span>{record?.device?.name}</span>
            </div>
          </Tooltip>
          {record?.device?.imageLink[0] != '' && record?.device?.imageLink[0].includes('.jpg', '.png') ? (
            <img src={record?.device?.imageLink[0]} alt='' style={{ width: '100px', height: 'auto' }} />
          ) : (
            ''
          )}
        </div>
      ),
      dataIndex: '',
    },
    {
      title: 'Locatie',
      dataIndex: 'workPlace',
      key: 'workPlace',
      render: (workPlace) => <div>{workPlace?.name}</div>,
    },
    {
      title: 'Cantitate',
      dataIndex: 'piece',
      key: 'piece',
      editable: true,
    },
    {
      title: 'Unitate De Masura',
      dataIndex: 'unitMeasure',
      key: 'unitMeasure',
      hidden: true,
    },
    {
      title: 'Cost Manopera Per Bucata (lei)',
      dataIndex: 'laborPrice',
      key: 'laborPrice',
      editable: true,
    },
    {
      title: 'Timp Manopera Estimat (minute)',
      dataIndex: 'laborTime',
      key: 'laborTime',
      editable: true,
    },
  ];
  //Mobile view render card
  const renderCard = (record: any) => (
    <Card key={record.id} className='deviceCard'>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          {record?.device?.imageLink[0]?.includes('.jpg', '.png') && (
            <img src={record.device.imageLink[0]} alt='device' className='image' />
          )}
        </Col>
        <Col span={24}>
          <Tooltip title={record.device?.name}>
            <div className='ellipsisCell'>
              <strong>Device: </strong> {record.device?.name}
            </div>
          </Tooltip>
        </Col>
        <Col span={24}>
          <strong>Locatie: </strong>
          {record.workPlace?.name}
        </Col>
        <Col span={12}>
          <strong>Cantitate: </strong>
          <br />
          <Button disabled className='disableButton'>
            <b>{record.piece}</b>
          </Button>
          <Select
            value={record?.unitMeasure || 'buc'}
            onChange={(newUnit: string) => handleUnitChange(record, newUnit)}
            className='input-width'
          >
            <Option value='buc'>buc</Option>
            <Option value='hour'>hour</Option>
          </Select>
        </Col>
        <Col span={12}>
          <strong>Cost Manopera: </strong>
          <br />
          <Button disabled className='disableButton'>
            <b>
              <ToolOutlined style={{ marginRight: 5 }} />
              {record.unitMeasure === 'buc'
                ? record.device.laborPriceSM + ' lei/buc'
                : Number.parseFloat((record.laborPrice / record.piece).toFixed(2)) + ' lei/ora'}
            </b>
          </Button>
          <InputNumber
            min={1}
            value={record.laborPrice}
            onChange={(value) => handleDeviceChange(record, value, 'laborPrice')}
            className='input-width'
          />
        </Col>
        <Col span={12}>
          <strong>Timp Manopera:</strong> <br />
          <Button disabled className='disableButton'>
            {' '}
            <b>
              <FieldTimeOutlined style={{ marginRight: 5 }} />
              {record.unitMeasure === 'buc' ? record.device.laborTimeSM + ' min/buc' : '60 min'}
            </b>
          </Button>
          <InputNumber
            min={1}
            value={record.laborTime}
            onChange={(value) => handleDeviceChange(record, value, 'laborTime')}
            className='input-width'
          />
        </Col>
      </Row>
    </Card>
  );

  //Mobile view summary row
  const renderSummary = () => {
    return (
      <Row>
        <Col span={24}>
          {addedDevices.filter((ad) => ad.unitMeasure === 'buc').length > 0 && (
            <div className='totalCard'>
              <strong className='totalLab'>Manopera bucata/ora</strong>
              <div className='totalLab2'>{calculateWeightedAverage('buc')} lei</div>
            </div>
          )}
        </Col>

        <Col span={24}>
          {addedDevices.filter((ad) => ad.unitMeasure === 'hour').length > 0 && (
            <div className='totalCard'>
              <div style={{ flex: 1 }}>
                <strong className='totalLab'>Manopera/ora</strong>
              </div>
              <div className='totalLab2'>{calculateWeightedAverage('hour')} lei</div>
            </div>
          )}
        </Col>

        <Col span={24}>
          <div className='total'>
            <div style={{ flex: 1 }}>
              <strong className='totalLab'>Total</strong>
            </div>
            <div style={{ flex: 1 }}>
              <strong className='totalLab2'>
                {addedDevices.reduce((total, item) => total + (item.laborTime ?? 0), 0)} minute
              </strong>
            </div>
            <div style={{ marginLeft: 'auto' }}>
              <strong className='totalLab2'>
                {Number.parseFloat(addedDevices.reduce((total, item) => total + (item.piece ?? 0), 0).toFixed(2))} lei
              </strong>
            </div>
          </div>
        </Col>
      </Row>
    );
  };

  //Large screnn  table view
  const renderTable = () => (
    <CustomTable
      rowClassName='table-row-dark'
      initialDataSource={addedDevices}
      columns={columnsFirstTable?.map((column, index) => ({
        ...column,
        key: index,
        render: column.render || ((text, record) => renderColumn(column, text, record)),
      }))}
      pagination={false}
      summary={() => (
        <>
          {addedDevices.filter((ad) => ad.unitMeasure == 'buc').length > 0 && (
            <Table.Summary.Row style={{ backgroundColor: '#e2e3e5' }}>
              <Table.Summary.Cell index={0}>
                <strong>Manopera bucata/ora </strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1}></Table.Summary.Cell>
              <Table.Summary.Cell index={2}></Table.Summary.Cell>
              <Table.Summary.Cell index={3}></Table.Summary.Cell>
              <Table.Summary.Cell index={4}>
                <strong>{calculateWeightedAverage('buc')} lei</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={5}></Table.Summary.Cell>
            </Table.Summary.Row>
          )}

          {addedDevices.filter((ad) => ad.unitMeasure == 'hour').length > 0 && (
            <Table.Summary.Row style={{ backgroundColor: '#e2e3e5' }}>
              <Table.Summary.Cell index={0}>
                <strong>Manopera/ora</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1}></Table.Summary.Cell>
              <Table.Summary.Cell index={2}></Table.Summary.Cell>
              <Table.Summary.Cell index={3}></Table.Summary.Cell>
              <Table.Summary.Cell index={4}>
                <strong>{calculateWeightedAverage('hour')} lei</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={5}></Table.Summary.Cell>
            </Table.Summary.Row>
          )}

          <Table.Summary.Row style={{ backgroundColor: '#e2e3e5' }}>
            <Table.Summary.Cell index={0}>
              <strong>Total</strong>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={1}></Table.Summary.Cell>
            <Table.Summary.Cell index={2}></Table.Summary.Cell>
            <Table.Summary.Cell index={3}>
              <strong>
                {Number.parseFloat(addedDevices.reduce((total, item) => total + item.piece, 0).toFixed(2))}
              </strong>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={4}>
              <strong>
                {Number.parseFloat(
                  (
                    addedDevices?.reduce((total, item) => total + item.laborPrice, 0) +
                    pmOffer?.pmOfferLaborExtras?.reduce((total, item) => total + item?.piece * item?.price, 0)
                  ).toFixed(2)
                )}{' '}
                lei
              </strong>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={5}>
              <strong>{addedDevices.reduce((total, item) => total + item.laborTime, 0)} minute</strong>
            </Table.Summary.Cell>
          </Table.Summary.Row>
        </>
      )}
    />
  );
  return (
    <div>
      {isMobile ? (
        <div>
          {addedDevices.map(renderCard)}
          {addedDevices.some((ad) => ad.unitMeasure === 'buc') && renderSummary()}
        </div>
      ) : (
        renderTable()
      )}
    </div>
  );
};

export default DevicesTable;
